/**
 * @fileoverview `DiscoverListView` — list projection of the discover surface.
 *
 * Renders the day-grouped event list with cursor-based "Load more" pagination.
 * Default view of `/discover`.
 *
 * Follows the View + Connected split required by `apps/web/CLAUDE.md`:
 *   - {@link DiscoverListViewView} — pure props, used by Storybook + tests.
 *     Owns grouping, day-label derivation, skeleton + empty-state rendering.
 *   - {@link DiscoverListView} (connected) — wires `useDiscoverParams` for
 *     reset behaviour and `useDiscoverEventsQuery` for cursor pagination.
 *     Accumulates pages in component state so the URL stays clean (cursor
 *     is intentionally not serialized — AC: "URL doesn't grow unbounded").
 *
 * Out of scope (tracked separately):
 *   - Results bar (count + view tabs + sort) — hoisted to `DiscoverTemplate`
 *     in WEB-270 so the tabs persist across List/Map/Calendar views.
 *   - Map view body — owned by M4.1 (`DiscoverMapView`, already shipped).
 *   - Calendar view body — M5.
 *   - The data fetching call site itself — uses M7.3 hook.
 *
 * @since WEB-67 (Discover · Tri-View Template — M3.3)
 * @since WEB-270 — `DiscoverResultsBar` hoisted out of this component into
 *   `DiscoverTemplate`. The list view no longer owns view/sort props.
 * @since WEB-676 — declared list tail (remainder + widen) replaces silent
 *   "Load more" when `meta.tasteMatchedTotal` leaves an honest remainder.
 */
'use client';

import * as React from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { CITY_RECORDS, type CityCode } from '@loci/city-editions';
import { formatEventTime, type DiscoverEvent } from '@loci/types';
import { addLocalDays, isLocalDateKey, localWeekday } from '@loci/validation';

import {
  useDiscoverEventsQuery,
  type DiscoverEventsQueryArgs,
  type DiscoverEventsResponse,
} from '@loci/api';

import { TextIcon } from '@/components/atoms/TextIcon';
import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';
import { DiscoverDaySection } from '@/components/molecules/DiscoverDaySection';
import { DiscoverEventCard } from '@/components/molecules/DiscoverEventCard';
import { DiscoverListSkeleton } from '@/components/molecules/DiscoverListSkeleton';
import { DiscoverNowMarker } from '@/components/molecules/DiscoverNowMarker';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import {
  useDiscoverErrorHost,
  useDiscoverRequestKey,
  useReportDiscoverQuery,
} from '@/lib/discover/discoverErrorHost';
import type { DiscoverErrorSummary } from '@/lib/discover/onDiscoverError';
import { useNetworkStatus } from '@/utils/offlineCache';
import {
  computeDiscoverListTailRemainder,
  countOrganicRows,
  formatDiscoverListTailRemainderLine,
  formatDiscoverListTailShowAll,
  formatDiscoverListTailWidenLabel,
  isDiscoverListTailMatchedTotalUnmeasurable,
  isDiscoverListTailPrimaryRow,
  LIST_TAIL_WIDEN_PROMPT,
  nextWidenDatePreset,
  resolveDiscoverListTailMatchedTotal,
  shouldHoldBackUnmatchedRows,
  shouldRenderDiscoverListTail,
} from '@/lib/discover/listTailCopy';
import { useDiscoverClock } from '@/lib/discover/DiscoverClockContext';
import { DISCOVER_PAGE_SIZE, buildDiscoverQueryArgs } from '@/lib/discover/queryArgs';
import { parseEventInstantMs } from '@/lib/discover/timelineClock';
import { buildStillAheadCopy, type TimelineGroupCopy } from '@/lib/discover/stillAheadHeading';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import {
  defaultDiscoverParams,
  serializeDiscoverParams,
  type DateValue,
  type DiscoverParams,
} from '@/lib/discover/url-state';

import { DiscoverEmptyState, type DiscoverEmptyStateKind } from './DiscoverEmptyState';

const PAGE_SIZE = DISCOVER_PAGE_SIZE;
/** COR-155: the MVP market's zone, read off its city record. */
const DEFAULT_CITY_TIMEZONE = CITY_RECORDS.CLT.timeZone;

const WEEKDAY_LABELS: readonly string[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Abbreviated month names — indexed by `Date.getUTCMonth()` (0–11). */
const MONTH_LABELS: readonly string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/* ─── Pure helpers (exported for tests) ────────────────────────────────── */

export interface DiscoverDayBucket {
  /** `YYYY-MM-DD` in the bucket's resolved local timezone. */
  readonly localDate: string;
  /** Events whose `startsAt` falls inside this local-date bucket. */
  readonly events: DiscoverEvent[];
}

/**
 * Group events by their local-date in the venue timezone. Stable order:
 * - Buckets sorted by `localDate` ascending.
 * - Events inside each bucket keep the input order (the caller controls sort
 *   on the server; we don't re-sort on the client).
 */
export function groupEventsByDay(events: readonly DiscoverEvent[]): DiscoverDayBucket[] {
  const buckets = new Map<string, DiscoverEvent[]>();
  for (const event of events) {
    const localDate = formatEventTime(event.startsAt, event.timezone).localDate;
    const existing = buckets.get(localDate);
    if (existing) {
      existing.push(event);
    } else {
      buckets.set(localDate, [event]);
    }
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([localDate, dayEvents]) => ({ localDate, events: dayEvents }));
}

/**
 * Today's local-date string in `cityTimezone` (`YYYY-MM-DD`). Pure helper
 * so tests can pass a fixed `now`.
 */
export function todayLocalDate(cityTimezone: string, now: Date = new Date()): string {
  return formatEventTime(now, cityTimezone).localDate;
}

/**
 * Build the day-section header view-model from a bucket. Adds the friendly
 * "Tonight · " / "Tomorrow · " prefix when the bucket falls on the current
 * or following local day in the city's timezone.
 *
 * DS 1.5.0 parity (FRE-907): the `disc-day` block splits the header across two
 * lines — `disc-day__label` ("Tonight · Wednesday") carries only the prefix +
 * weekday, and `disc-day__sub` ("APR 29 · 18 EVENTS") leads with the uppercase
 * calendar date. `buildDayLabel` therefore returns the date as a separate,
 * already-uppercased `monthDayLabel` ("APR 29") rather than appending it to
 * `dayLabel`.
 *
 * The calendar date is RELOCATED, not dropped: WEB-293 (audit item 26) added it
 * so a same-Monday section is distinguishable from a next-Monday one. That
 * disambiguation is preserved — the date now lives in the sub-line instead of
 * the label. Output shapes:
 *   - today:    label "Tonight · Wednesday"  + monthDayLabel "MAY 20"
 *   - tomorrow: label "Tomorrow · Thursday"  + monthDayLabel "MAY 21"
 *   - further:  label "Friday"               + monthDayLabel "MAY 22"
 */
export function buildDayLabel(
  bucket: DiscoverDayBucket,
  cityTimezone: string,
  todayLocal: string,
): { dayNumber: number; dayLabel: string; monthDayLabel: string; count: number } {
  const [, monthStr, dayStr] = bucket.localDate.split('-');
  const dayNumber = Number.parseInt(dayStr ?? '', 10) || 1;

  // Weekday from the calendar date itself (API-1820 shared `localWeekday`),
  // so it is timezone-stable: only the weekday index is needed.
  const month = Number.parseInt(monthStr ?? '', 10);
  const day = Number.parseInt(dayStr ?? '', 10);
  let weekday = 'Day';
  let monthDayLabel = '';
  if (isLocalDateKey(bucket.localDate)) {
    weekday = WEEKDAY_LABELS[localWeekday(bucket.localDate)] ?? 'Day';
    const monthLabel = MONTH_LABELS[month - 1];
    if (monthLabel) {
      // Uppercase month for the `disc-day__sub` line, e.g. "MAY 20".
      monthDayLabel = `${monthLabel} ${day}`;
    }
  }

  let prefix = '';
  if (bucket.localDate === todayLocal) {
    prefix = 'Tonight · ';
  } else if (bucket.localDate === addLocalDays(todayLocal, 1)) {
    prefix = 'Tomorrow · ';
  }

  return {
    dayNumber,
    dayLabel: `${prefix}${weekday}`,
    monthDayLabel,
    count: bucket.events.length,
  };
}

/**
 * Single-day date scope (WEB-671 / DPL-20). Timeline groups (In progress /
 * NOW / Still ahead) only apply inside one local day. Multi-day presets and
 * the unfiltered default window keep calendar day heads.
 *
 * `tonight` / `tomorrow` are single-day keywords. An ISO `YYYY-MM-DD..YYYY-MM-DD`
 * range is single-day only when both ends are the same calendar date.
 */
export function isSingleDayScope(date: DateValue | undefined): boolean {
  if (date === 'tonight' || date === 'tomorrow') return true;
  if (typeof date === 'string' && date.includes('..')) {
    const [start, end] = date.split('..');
    return Boolean(start && end && start === end);
  }
  return false;
}

export interface TimelinePartition {
  readonly inProgress: DiscoverEvent[];
  readonly stillAhead: DiscoverEvent[];
}

/**
 * Split events across the NOW marker on the event interval (WEB-784 / DPL-20).
 * Comparison is the ISO instant (`Date.parse`) — do not re-derive "local now"
 * from the browser zone.
 *
 * - TBA / unparseable start → Still ahead (PIP-98).
 * - `startsAt > now` → Still ahead.
 * - `startsAt <= now` → In progress, including ended rows that collapse
 *   behind that head. `.dv-ev--live` is a card concern: interval
 *   `startsAt <= now < endsAt` with a published end.
 */
export function partitionTimelineEvents(
  events: readonly DiscoverEvent[],
  now: Date,
): TimelinePartition {
  const inProgress: DiscoverEvent[] = [];
  const stillAhead: DiscoverEvent[] = [];
  const nowMs = now.getTime();
  for (const event of events) {
    const startMs = parseEventInstantMs(event.startsAt);
    // PIP-98: a TBA start fabricates local midnight. Do not claim it has
    // already begun — the row itself refuses the live variant for the same
    // reason (`startTimeKnown === false`).
    const timeKnown = event.startTimeKnown ?? true;
    if (!timeKnown || Number.isNaN(startMs) || startMs > nowMs) {
      stillAhead.push(event);
    } else {
      inProgress.push(event);
    }
  }
  return { inProgress, stillAhead };
}

export {
  buildStillAheadCopy,
  formatShortWeekday,
  stillAheadHeadingInstant,
} from '@/lib/discover/stillAheadHeading';
export type { TimelineGroupCopy } from '@/lib/discover/stillAheadHeading';

export function buildInProgressCopy(count: number): TimelineGroupCopy {
  const noun = count === 1 ? 'event' : 'events';
  return {
    title: 'In progress',
    detail: `Started before now · ${count} ${noun}`,
  };
}

/**
 * Resolve the empty-state kind from the active error + connectivity. Pure +
 * exported so the precedence is greppable and unit-testable instead of buried
 * in a nested ternary. Precedence (highest first):
 *
 *   1. No empty error state / aborted → `no-match` only when there is no
 *      error. Abort never flashes an error empty state (WEB-938).
 *   2. `rate-limited` (429) → always its own countdown treatment, regardless
 *      of connectivity (a 429 is a server signal, not a network one).
 *   3. `network` while offline → `offline`.
 *   4. `network` while online, `unknown` either way, `4xx`/`5xx` while
 *      online → `request-failed`.
 *   5. `4xx` / `5xx` while the device reports offline → `offline`.
 *   Only a successful empty response may render `no-match`.
 */
export function resolveEmptyStateKind(
  hasEmptyErrorState: boolean,
  lastError: DiscoverErrorSummary | null,
  isOnline: boolean,
): DiscoverEmptyStateKind {
  if (!hasEmptyErrorState || lastError === null) return 'no-match';
  switch (lastError.kind) {
    case 'rate-limited':
      return 'rate-limited';
    case 'aborted':
      return 'no-match';
    case 'unknown':
      return 'request-failed';
    case 'network':
    case '4xx':
    case '5xx':
      return isOnline ? 'request-failed' : 'offline';
    default: {
      const _exhaustive: never = lastError.kind;
      return _exhaustive;
    }
  }
}

/* ─── Pure View ────────────────────────────────────────────────────────── */

export interface DiscoverListViewViewProps {
  /** Already-merged events to render (ordered by sort + cursor pages appended). */
  events: DiscoverEvent[];
  /**
   * Total events across all pages. Drives the load-more visibility computation
   * (`hasMore && events.length < total`). Note: the surface-level count line
   * lives in `DiscoverResultsBar` (hoisted to `DiscoverTemplate` in WEB-270),
   * not here.
   */
  total: number;
  /** City segment used to build per-event detail links. */
  cityCode: CityCode;
  /**
   * Local timezone used to resolve "Tonight · " / "Tomorrow · " prefixes on
   * day headers. Defaults to Charlotte's zone (`CITY_RECORDS.CLT.timeZone`) when omitted.
   */
  cityTimezone?: string;
  /** True on the very first fetch (no events yet). Renders skeleton rows. */
  isLoading?: boolean;
  /**
   * True when an additional page is in flight. Renders the load-more button
   * in a disabled "Loading…" state but keeps the existing list visible.
   */
  isFetchingMore?: boolean;
  /** True when the cursor is non-null and another page is available. */
  hasMore?: boolean;
  /** Number of additional events the next page would load (defaults to 50). */
  nextPageSize?: number;
  /** Fired when the user clicks "Load N more events". */
  onLoadMore?: () => void;
  /** Fired when the user clicks "Reset" inside the empty state. */
  onReset: () => void;
  /**
   * Empty-state kind. Defaults to `'no-match'` so existing call sites keep
   * the legacy "widen your filter" body. When the connected component
   * detects a fetch error it passes `'request-failed'` so the empty state
   * shows the Retry button instead.
   */
  emptyStateKind?: DiscoverEmptyStateKind;
  /**
   * Fired when the user clicks "Retry" inside the request-failed empty
   * state. Optional — the connected component supplies it; stories may
   * omit.
   */
  onRetry?: () => void;
  /**
   * Seconds to seed the rate-limit countdown with. Only meaningful when
   * `emptyStateKind === 'rate-limited'`; forwarded verbatim to
   * `DiscoverEmptyState` which owns the per-second tick.
   */
  retryAfterSeconds?: number;
  /** Optional class merged onto the outer container. */
  className?: string;
  /**
   * Shared timeline instant (WEB-779).
   * - `Date` — frozen / provider-live instant.
   * - `null` — provider-pending: keep the hydration-safe pending state; do not
   *   sample a local clock.
   * - omitted (`undefined`) — uncontrolled standalone stories: sample a local
   *   clock after mount.
   */
  now?: Date | null;
  /**
   * Active date filter from the URL. Single-day values (`tonight`,
   * `tomorrow`, or an ISO range whose ends match) switch the list to
   * In-progress / NOW / Still-ahead groups (WEB-671). Multi-day presets and
   * an unset date keep calendar day heads.
   */
  date?: DateValue;
  /**
   * Full URL-state params for the no-match empty copy (WEB-939). When omitted,
   * the empty state names the date scope from {@link date} only.
   */
  params?: DiscoverParams;
  /** Optional venue id → name map for empty-state naming. */
  venueNames?: ReadonlyMap<string, string>;
  /**
   * WEB-676 / DPL-26 — corpus-wide taste-matched total from
   * `meta.tasteMatchedTotal`. Same number the results bar uses. `null`
   * (FRE-917 gate miss — anonymous, or no taste signal yet) does NOT hide the
   * tail: WEB-791 counts the remainder against the rows already loaded
   * instead, and keeps every loaded row visible. See `listTailCopy.ts`.
   */
  tasteMatchedTotal?: number | null;
  /**
   * WEB-791 — the viewer HAS a taste signal but the corpus-wide matched count
   * could not be measured (`meta.personalized === true` with a `null`
   * `meta.tasteMatchedTotal`). Distinct from the anonymous `null`: those rows
   * can carry real taste matches, so the tail stays silent rather than caption
   * them `NO TASTE SIGNAL YET`. Pagination falls back to load-more.
   */
  matchedTotalUnmeasurable?: boolean;
  /**
   * True after the user clicks "Show all". Reveals unmatched rows and
   * hides the tail (everything in range is now shown).
   */
  showAllExpanded?: boolean;
  /**
   * True when a Show-all page drain failed. Restores the Load-more
   * control so the remaining cursor can be retried.
   */
  showAllDrainFailed?: boolean;
  /** Fired when the user clicks "Show all N". */
  onShowAll?: () => void;
  /** Fired when the user clicks the widen-search action. */
  onWidenSearch?: () => void;
}

/**
 * Pure render layer for the discover list view. No data fetching, no URL
 * state — all dependencies arrive via props. The connected `DiscoverListView`
 * below wires this to RTK Query + `useDiscoverParams`.
 */
export function DiscoverListViewView({
  events,
  total,
  cityCode,
  cityTimezone = DEFAULT_CITY_TIMEZONE,
  isLoading = false,
  isFetchingMore = false,
  hasMore = false,
  nextPageSize = PAGE_SIZE,
  onLoadMore,
  onReset,
  emptyStateKind = 'no-match',
  onRetry,
  retryAfterSeconds,
  className,
  now,
  date,
  params,
  venueNames,
  tasteMatchedTotal = null,
  matchedTotalUnmeasurable = false,
  showAllExpanded = false,
  showAllDrainFailed = false,
  onShowAll,
  onWidenSearch,
}: DiscoverListViewViewProps): React.ReactElement {
  const isTimeline = isSingleDayScope(date);
  // Sponsored slots ride along in `events` but are not part of the population
  // `meta.total` counts, so the head must be measured on organic rows only —
  // otherwise a page of 50 organic + 10 sponsored reads as 60 against a total
  // of 55 and retires the tail with 5 organic events still unloaded.
  const organicLoadedCount = React.useMemo(() => countOrganicRows(events), [events]);
  const tailCounts = {
    total,
    tasteMatchedTotal,
    loadedCount: organicLoadedCount,
    matchedTotalUnmeasurable,
  } as const;
  const tailRemainder = computeDiscoverListTailRemainder(tailCounts);
  const showTail = !showAllExpanded && shouldRenderDiscoverListTail(tailCounts);
  // Unmatched rows only wait behind "Show all" when a taste-matched head
  // exists to hold them back from (WEB-791). Without a signal every loaded row
  // IS the head, and filtering would empty the list.
  const holdBackUnmatched = showTail && shouldHoldBackUnmatchedRows(tailCounts);
  const visibleEvents = React.useMemo(
    () => (holdBackUnmatched ? events.filter(isDiscoverListTailPrimaryRow) : events),
    [holdBackUnmatched, events],
  );
  const widenPreset = nextWidenDatePreset(date);
  const widenLabel = widenPreset ? formatDiscoverListTailWidenLabel(widenPreset) : null;
  // WEB-779: group membership, row countdown, and the NOW marker share this
  // instant. Production threads the Discover clock (`useDiscoverClock`); tests
  // inject `now`. `null` is provider-pending — stay hydration-safe and do not
  // sample a local clock. Omitted `now` (standalone stories) may sample after
  // mount. Every row sits Still ahead until a shared live instant lands.
  const [uncontrolledNow, setUncontrolledNow] = React.useState<Date | null>(null);
  React.useEffect(() => {
    if (now instanceof Date || now === null) {
      return;
    }
    if (isTimeline) {
      setUncontrolledNow(new Date());
    }
  }, [now, isTimeline]);
  const groupingNow = now === undefined ? uncontrolledNow : now;

  const buckets = React.useMemo(() => groupEventsByDay(visibleEvents), [visibleEvents]);
  const todayLocal = React.useMemo(
    () => todayLocalDate(cityTimezone, now instanceof Date ? now : undefined),
    [cityTimezone, now],
  );
  const partition = React.useMemo(() => {
    if (!isTimeline)
      return { inProgress: [] as DiscoverEvent[], stillAhead: [] as DiscoverEvent[] };
    if (!groupingNow) return { inProgress: [] as DiscoverEvent[], stillAhead: visibleEvents };
    return partitionTimelineEvents(visibleEvents, groupingNow);
  }, [isTimeline, groupingNow, visibleEvents]);
  const remainder = Math.max(0, total - events.length);
  const inProgressCopy = buildInProgressCopy(partition.inProgress.length);
  const stillAheadCopy = buildStillAheadCopy(
    date,
    groupingNow,
    partition.stillAhead.length,
    remainder,
    cityTimezone,
  );

  const showSkeleton = isLoading && events.length === 0;
  // Surface the empty state on either:
  //   - `total === 0` after a successful response, or
  //   - an error kind (`request-failed` / `rate-limited`) with no rows, so
  //     the user sees the error / rate-limit body instead of a stale (or
  //     empty) list when a 4xx / 5xx / 429 came back.
  const isErrorEmptyKind =
    emptyStateKind === 'request-failed' ||
    emptyStateKind === 'rate-limited' ||
    emptyStateKind === 'offline';
  const showEmpty =
    !isLoading &&
    ((total === 0 && events.length === 0) || (isErrorEmptyKind && events.length === 0));

  return (
    <section
      data-testid="discover-list-view"
      data-discover-list-view="true"
      // WEB-1081: no ≤720 bottom padding — the results bar is sticky under the
      // nav at every width, so there is no floor bar to clear. `<main>` owns the
      // dock reservation (WEB-727); the Locus-launcher clearance keys on this
      // hook in `styles/overrides/discover-locus-clearance.css`.
      className={cn('flex w-full flex-col', className)}
      aria-label="Discover events list"
    >
      {showSkeleton ? (
        <DiscoverListSkeleton />
      ) : showEmpty ? (
        <DiscoverEmptyState
          kind={emptyStateKind}
          retryAfterSeconds={retryAfterSeconds}
          onReset={onReset}
          onRetry={onRetry}
          params={params ?? (date === undefined ? undefined : { ...defaultDiscoverParams(), date })}
          venueNames={venueNames}
          onWiden={emptyStateKind === 'no-match' ? onWidenSearch : undefined}
        />
      ) : (
        <>
          <ol
            className="flex w-full list-none flex-col gap-0 p-0"
            data-testid="discover-list-view-buckets"
          >
            {isTimeline ? (
              <TimelineGroups
                inProgress={partition.inProgress}
                stillAhead={partition.stillAhead}
                inProgressCopy={inProgressCopy}
                stillAheadCopy={stillAheadCopy}
                cityCode={cityCode}
                cityTimezone={cityTimezone}
                now={groupingNow}
              />
            ) : (
              buckets.map((bucket) => {
                const dateLabel = buildDayLabel(bucket, cityTimezone, todayLocal);
                return (
                  <li key={bucket.localDate} className="contents">
                    <DiscoverDaySection dateLabel={dateLabel} localDate={bucket.localDate}>
                      {bucket.events.map((event) => (
                        <DiscoverEventCard
                          key={event.id}
                          event={event}
                          cityCode={cityCode}
                          timeZone={cityTimezone}
                        />
                      ))}
                    </DiscoverDaySection>
                  </li>
                );
              })
            )}
          </ol>

          {showTail ? (
            <DiscoverListTail
              total={total}
              remainder={tailRemainder}
              matched={tasteMatchedTotal ?? 0}
              date={date}
              widenLabel={widenLabel}
              onShowAll={onShowAll}
              onWidenSearch={onWidenSearch}
            />
          ) : hasMore && (!showAllExpanded || showAllDrainFailed) ? (
            <div className="flex items-center justify-center px-5 py-6">
              <Button
                type="button"
                variant="ghost"
                onClick={onLoadMore}
                disabled={isFetchingMore || onLoadMore === undefined}
                data-testid="discover-list-view-load-more"
              >
                {isFetchingMore ? (
                  'Loading'
                ) : (
                  <span className="inline-flex items-center gap-1">
                    {`Load ${nextPageSize} more events`}
                    <TextIcon />
                  </span>
                )}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function TimelineGroups({
  inProgress,
  stillAhead,
  inProgressCopy,
  stillAheadCopy,
  cityCode,
  cityTimezone,
  now,
}: {
  inProgress: readonly DiscoverEvent[];
  stillAhead: readonly DiscoverEvent[];
  inProgressCopy: TimelineGroupCopy;
  stillAheadCopy: TimelineGroupCopy;
  cityCode: CityCode;
  cityTimezone: string;
  now?: Date | null;
}): React.ReactElement {
  return (
    <>
      {inProgress.length > 0 ? (
        <li className="contents">
          <DiscoverDaySection
            variant="timeline"
            timelineKind="in-progress"
            title={inProgressCopy.title}
            detail={inProgressCopy.detail}
          >
            {inProgress.map((event) => (
              <DiscoverEventCard
                key={event.id}
                event={event}
                cityCode={cityCode}
                now={now}
                timeZone={cityTimezone}
              />
            ))}
          </DiscoverDaySection>
        </li>
      ) : null}
      <li className="contents">
        <DiscoverNowMarker timeZone={cityTimezone} now={now} />
      </li>
      {stillAhead.length > 0 ? (
        <li className="contents">
          <DiscoverDaySection
            variant="timeline"
            timelineKind="still-ahead"
            title={stillAheadCopy.title}
            detail={stillAheadCopy.detail}
          >
            {stillAhead.map((event) => (
              <DiscoverEventCard
                key={event.id}
                event={event}
                cityCode={cityCode}
                now={now}
                timeZone={cityTimezone}
              />
            ))}
          </DiscoverDaySection>
        </li>
      ) : null}
    </>
  );
}

const TAIL_META_CLASS = 'm-0 font-mono text-caption tracking-[0.04em] text-fg-3';
// WEB-1076: 36px actions (the v3.1 `.dv-tail a` height); `.a-hit-floor` is
// their coarse-pointer 44px hit box.
const TAIL_ACTION_CLASS = cn(
  'a-hit-floor inline-flex min-h-9 items-center font-mono text-caption tracking-[0.04em]',
  'text-ds-accent underline-offset-4 hover:underline',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
);

function DiscoverListTail({
  total,
  remainder,
  matched,
  date,
  widenLabel,
  onShowAll,
  onWidenSearch,
}: {
  total: number;
  remainder: number;
  matched: number;
  date: DateValue | undefined;
  widenLabel: string | null;
  onShowAll?: () => void;
  onWidenSearch?: () => void;
}): React.ReactElement {
  const remainderLine = formatDiscoverListTailRemainderLine(remainder, date);
  const showAllLabel = formatDiscoverListTailShowAll(total);

  return (
    <footer
      data-testid="discover-list-tail"
      data-remainder={remainder}
      data-in-range={total}
      data-matched={matched}
      className="flex w-full flex-col gap-4 border-t border-loci-border px-5 py-6"
    >
      <div
        data-testid="discover-list-tail-remainder"
        data-remainder={remainder}
        className="flex flex-col gap-3 min-[721px]:flex-row min-[721px]:items-center min-[721px]:justify-between"
      >
        <p className={TAIL_META_CLASS}>{remainderLine}</p>
        <button
          type="button"
          data-testid="discover-list-tail-show-all"
          className={TAIL_ACTION_CLASS}
          onClick={onShowAll}
          disabled={onShowAll === undefined}
        >
          <span className="inline-flex items-center gap-1">
            {showAllLabel}
            <TextIcon />
          </span>
        </button>
      </div>
      {widenLabel ? (
        <div
          data-testid="discover-list-tail-widen"
          className="flex flex-col gap-2 min-[721px]:flex-row min-[721px]:items-center min-[721px]:gap-3"
        >
          <p className={TAIL_META_CLASS}>{LIST_TAIL_WIDEN_PROMPT}</p>
          <button
            type="button"
            data-testid="discover-list-tail-widen-action"
            className={TAIL_ACTION_CLASS}
            onClick={onWidenSearch}
            disabled={onWidenSearch === undefined}
          >
            <span className="inline-flex items-center gap-1">
              <TextIcon />
              {widenLabel}
            </span>
          </button>
        </div>
      ) : null}
    </footer>
  );
}

/* ─── Connected ────────────────────────────────────────────────────────── */

export interface DiscoverListViewProps {
  /** City segment used to build per-event detail links. */
  cityCode: CityCode;
  /**
   * Local timezone used to derive "Tonight · " / "Tomorrow · " day prefixes.
   * Defaults to Charlotte's zone (`CITY_RECORDS.CLT.timeZone`) when omitted.
   */
  cityTimezone?: string;
  /**
   * Server-rendered first page so the SSR markup paints immediately and the
   * RTK Query hydration doesn't flash a skeleton on first load.
   */
  initialEvents?: DiscoverEvent[];
  /** Total matching events from the server-rendered first page. */
  initialTotal?: number;
  /**
   * WEB-676 — SSR `meta.tasteMatchedTotal` so the tail can paint before
   * the shared RTK subscription resolves. `null` / omitted = no tail.
   */
  initialTasteMatchedTotal?: number | null;
  /** Cursor for the next page (or `null` at end of list). */
  initialCursor?: string | null;
  /** Optional class merged onto the outer container. */
  className?: string;
}

interface PaginationState {
  /** Cursor used for the *current* in-flight (or last completed) request. */
  cursor: string | null;
  /** Accumulated events across every page fetched so far. */
  events: DiscoverEvent[];
  /** Cursor for the next page; mirrors the latest `meta.cursor` from RTK. */
  nextCursor: string | null;
  /** Total across all pages — last value from `meta.total`. */
  total: number;
  /** Snapshot of params used for the accumulated pages — guards against drift. */
  paramsKey: string;
}

/**
 * Anonymous base-shape helper (API-1415). Wraps `buildDiscoverQueryArgs` for
 * the contract test in `__tests__/DiscoverListView.test.tsx`, which asserts the
 * anchor + date mapping without driving RTK Query through `act()`.
 *
 * NOTE (WEB-450): the live render no longer subscribes through this — it uses
 * `useDiscoverEventArgs`, which threads the connected viewer id into the cache
 * key. This wrapper stays the anonymous (no viewer id) base shape the test
 * pins.
 */
export function buildQueryArgs(
  params: DiscoverParams,
  cursor: string | null,
  cityCode: CityCode,
  today: Date = new Date(),
): DiscoverEventsQueryArgs {
  return buildDiscoverQueryArgs(params, cityCode, cursor, today);
}

/**
 * Stable serialization of `DiscoverParams` for change-detection. Keeps the
 * cursor *out* so cursor advances inside the same filter set don't reset the
 * accumulator. Mirrors the URL-state ordering for human readability in tests.
 *
 * **INVARIANT (FRE-1310): every field `buildDiscoverQueryArgs` reads must
 * appear here, and nothing else.** This key decides whether the pagination
 * accumulator resets, so a query-affecting field that is missing leaves the
 * key unchanged when the user toggles it: the reset effect no-ops, the stale
 * cursor survives, and the newly-filtered page is APPENDED onto rows fetched
 * under the old filter. Conversely a field the query ignores (`view`,
 * `radius_km`) must stay out, or a no-op toggle would throw away pages the
 * user already loaded. The invariant is enforced behaviorally — not by a
 * hand-maintained list — in `__tests__/DiscoverListView.test.tsx`
 * ("paramsCacheKey ↔ buildDiscoverQueryArgs"), which walks every key of
 * `DiscoverParams` and fails the build when a new one is added without a
 * decision here.
 */
export function paramsCacheKey(params: DiscoverParams): string {
  return JSON.stringify({
    category: [...params.category].sort(),
    neighborhood: [...params.neighborhood].sort(),
    // FRE-1310: a field missing here once appended filtered pages onto stale
    // rows, which is why the invariant is now enforced by a derived test
    // instead of by remembering. `venue` has no chip rail since WEB-662, but it is still
    // parsed, still forwarded by `buildDiscoverQueryArgs`, and still honoured
    // end-to-end — so a `?venue=…` deep link or a saved search that carries
    // one narrows the results, and omitting it here mixed venue-filtered rows
    // into an unfiltered accumulator.
    venue: [...params.venue].sort(),
    // FRE-1310: `followedOnly` is the same defect with a LIVE producer — the
    // "Only who I follow" toggle (WEB-662) — so it was reachable by a plain
    // click after "Load more", not just by deep link. Normalized to a boolean
    // so the `false` and `undefined` off-states share one key, mirroring the
    // absence-is-off contract `buildDiscoverQueryArgs` puts on the wire.
    followedOnly: params.followedOnly === true,
    // FRE-1310: a "Search this area" redo (FRE-1103) re-centers the query on
    // a new lat/lng/radius, so it changes the result set as decisively as any
    // chip. Serialized field-by-field rather than by object identity, which a
    // fresh-but-equal viewport object would otherwise churn.
    viewport: params.viewport
      ? [params.viewport.lat, params.viewport.lng, params.viewport.radiusKm]
      : null,
    date: params.date ?? null,
    price: params.price ?? null,
    // WEB-1230: the "+N not listed" toggle widens the result set, but only
    // under a preset — exactly when `buildDiscoverQueryArgs` forwards it.
    priceUnlisted: params.price !== undefined && params.priceUnlisted === true,
    q: params.q ?? null,
    sort: params.sort,
  });
}

function makeInitialPaginationState(
  initialEvents: readonly DiscoverEvent[] | undefined,
  initialTotal: number | undefined,
  initialCursor: string | null | undefined,
  paramsKey: string,
): PaginationState {
  return {
    cursor: null,
    events: initialEvents ? [...initialEvents] : [],
    nextCursor: initialCursor ?? null,
    total: initialTotal ?? initialEvents?.length ?? 0,
    paramsKey,
  };
}

/**
 * Connected discover list view. Reads URL state via `useDiscoverParams`,
 * fires `useDiscoverEventsQuery` whenever filters or the active cursor
 * change, and accumulates pages in component state so the URL never grows
 * with a serialized cursor.
 *
 * Key invariants:
 * - **First paint matches SSR.** When `initialEvents` is provided and no
 *   page change has happened yet, we render those rows directly without
 *   waiting for RTK Query — the AC requires day-grouped output at first
 *   paint.
 * - **Filter change resets the accumulator.** Any `paramsKey` change (sort,
 *   date, category…) drops the accumulated pages back to the next fresh
 *   server response. Cursor is *not* part of the key.
 * - **`onLoadMore` advances the cursor.** Setting `cursor` triggers a new
 *   query; on success we append the new page and update `nextCursor`.
 */
export function DiscoverListView({
  cityCode,
  cityTimezone,
  initialEvents,
  initialTotal,
  initialTasteMatchedTotal = null,
  initialCursor,
  className,
}: DiscoverListViewProps): React.ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const { params, replaceParams } = useDiscoverParams();
  const clock = useDiscoverClock();
  const resolvedTimezone = cityTimezone ?? clock.timeZone;
  const currentParamsKey = React.useMemo(
    () => `${cityCode}:${paramsCacheKey(params)}`,
    [cityCode, params],
  );

  const [pagination, setPagination] = React.useState<PaginationState>(() =>
    makeInitialPaginationState(initialEvents, initialTotal, initialCursor, currentParamsKey),
  );
  const [showAllExpanded, setShowAllExpanded] = React.useState(false);
  const [showAllDrainFailed, setShowAllDrainFailed] = React.useState(false);
  const drainRemainingPagesRef = React.useRef(false);

  // Reset accumulator when params change (filter/sort/date/etc).
  React.useEffect(() => {
    setPagination((prev) => {
      if (prev.paramsKey === currentParamsKey) return prev;
      return {
        cursor: null,
        events: [],
        nextCursor: null,
        total: 0,
        paramsKey: currentParamsKey,
      };
    });
    drainRemainingPagesRef.current = false;
    setShowAllExpanded(false);
    setShowAllDrainFailed(false);
  }, [currentParamsKey]);

  // WEB-450: subscribe via the shared `useDiscoverEventArgs` hook so the
  // connected viewer id is threaded into the cache key identically to every
  // other discover subscriber. The exported `buildQueryArgs` below stays the
  // anonymous base-shape helper the contract test asserts against.
  //
  // FRE-1310: gate the cursor on the accumulator being CAUGHT UP. The reset
  // effect above only *queues* a state update, so on the render where
  // `currentParamsKey` has already changed, `pagination.cursor` is still the
  // previous filter set's cursor. RTK Query's subscription effect runs in that
  // same commit — after the reset effect, but before the re-render it
  // schedules — so it would `initiate` one request pairing the NEW filters
  // with the OLD cursor: a page offset into a result set that no longer
  // exists. Its response never reaches the list (the next render unsubscribes
  // that key), which is exactly what makes it easy to miss — it is a wasted
  // round trip, and one the backend may reject outright on a cursor it cannot
  // resolve against the new scope. Falling back to `null` requests page one,
  // which is what the pending reset is about to ask for anyway.
  const activeCursor = pagination.paramsKey === currentParamsKey ? pagination.cursor : null;
  const queryArgs = useDiscoverEventArgs(params, cityCode, activeCursor);

  // We always issue the query — even on first render with `initialEvents`,
  // RTK Query will hit its cache (M7.4 server cache hydrates the same key)
  // and dedupe instantly. If it returns fresh data we append/replace.
  const queryResult = useDiscoverEventsQuery(queryArgs);
  const { data, currentData, error, isLoading, isFetching, refetch } = queryResult;

  // WEB-268 / WEB-1096: the toast + 4xx revert belong to the template's ONE
  // `DiscoverErrorHost`, which arms itself on every URL change (rail, drawer,
  // map, results bar, reset). The list arms it only for actions that keep the
  // URL — load more, retry, show all — and reports its own cursor pages, a
  // cache entry the host's subscription cannot see. Same handler, so one
  // toast per error even when both see the page-one entry.
  const { lastError, recordUserAction, reporter } = useDiscoverErrorHost();
  const requestKey = useDiscoverRequestKey(queryArgs);
  useReportDiscoverQuery(reporter, { requestKey, error, isFetching, currentData });
  // Real connectivity signal (navigator.onLine + online/offline events). Used
  // only to *upgrade* an existing fetch error to the calmer offline copy —
  // never to gate fetching, and never on its own (a captive-portal can report
  // `onLine: true` while requests fail, and `onLine: false` can false-positive,
  // so we require a real error alongside it). WEB-468.
  const { isOnline } = useNetworkStatus();

  // Merge query results into pagination state. Treats the accumulator as
  // the source of truth for what's rendered.
  React.useEffect(() => {
    if (!data) return;
    setPagination((prev) => mergeQueryIntoState(prev, data, currentParamsKey));
  }, [data, currentParamsKey]);

  const handleLoadMore = React.useCallback(() => {
    if (showAllDrainFailed) {
      // The failed drain already advanced `cursor` to the bad page.
      // Refetch that page and resume draining remaining cursors.
      recordUserAction();
      setShowAllDrainFailed(false);
      drainRemainingPagesRef.current = true;
      void refetch();
      return;
    }
    if (!pagination.nextCursor) return;
    // Load-more is a user-initiated action — arm the host so a 4xx on the
    // follow-up page surfaces a toast. Armed only once a request will follow.
    recordUserAction();
    setPagination((prev) =>
      prev.cursor === prev.nextCursor ? prev : { ...prev, cursor: prev.nextCursor },
    );
  }, [recordUserAction, pagination.nextCursor, refetch, showAllDrainFailed]);

  // A URL change: the error host arms itself on the transition (WEB-1096).
  const handleReset = React.useCallback(() => {
    replaceParams(defaultDiscoverParams());
  }, [replaceParams]);

  const handleRetry = React.useCallback(() => {
    // Retry is a user-initiated action — re-arm the toast gating so a
    // second failure surfaces, then re-issue the query.
    recordUserAction();
    void refetch();
  }, [recordUserAction, refetch]);

  const handleShowAll = React.useCallback(() => {
    setShowAllExpanded(true);
    // User-initiated reveal. Drain every remaining page so "Show all N"
    // matches the in-range total instead of stopping after one 50-row page.
    if (!pagination.nextCursor) return;
    recordUserAction();
    drainRemainingPagesRef.current = true;
    setPagination((prev) =>
      prev.cursor === prev.nextCursor ? prev : { ...prev, cursor: prev.nextCursor },
    );
  }, [recordUserAction, pagination.nextCursor]);

  React.useEffect(() => {
    if (!drainRemainingPagesRef.current) return;
    // RTK Query 2.12 keeps the previous `error` while `isFetching` is true
    // during `refetch()`. Only a settled error is a drain failure.
    if (isFetching) return;
    if (error) {
      drainRemainingPagesRef.current = false;
      setShowAllDrainFailed(true);
      return;
    }
    if (!pagination.nextCursor) {
      drainRemainingPagesRef.current = false;
      return;
    }
    if (pagination.cursor === pagination.nextCursor) return;
    recordUserAction();
    setPagination((prev) =>
      prev.cursor === prev.nextCursor ? prev : { ...prev, cursor: prev.nextCursor },
    );
  }, [error, recordUserAction, isFetching, pagination.cursor, pagination.nextCursor]);

  const handleWidenSearch = React.useCallback(() => {
    const nextDate = nextWidenDatePreset(params.date);
    if (nextDate === null) return;
    // `push` (not `replace`) so browser Back restores the previous scope.
    const next = { ...params, date: nextDate };
    const serialized = serializeDiscoverParams(next).toString();
    const href = serialized.length > 0 ? `${pathname}?${serialized}` : pathname;
    router.push(href, { scroll: false });
  }, [params, pathname, router]);

  const events = pagination.events;
  const total = pagination.total;
  const tasteMatchedTotal = resolveDiscoverListTailMatchedTotal({
    live: data?.meta?.tasteMatchedTotal,
    initial: initialTasteMatchedTotal,
    personalized: data?.meta?.personalized,
    queryResolved: data !== undefined,
  });
  // A `null` matched total means two opposite things — anonymous/soaking, or a
  // signal-bearing viewer whose corpus count failed to resolve. Only the former
  // may be described as "no taste signal yet" (WEB-791).
  const matchedTotalUnmeasurable = isDiscoverListTailMatchedTotalUnmeasurable({
    personalized: data?.meta?.personalized,
    matchedTotal: tasteMatchedTotal,
  });
  const hasMore = pagination.nextCursor !== null && events.length < total;
  // First-page loading: nothing rendered yet AND query is loading.
  const firstPageLoading = isLoading && events.length === 0;
  // Subsequent-page loading: cursor advance triggered a new fetch.
  const loadingMore = isFetching && !isLoading && events.length > 0;

  // Surface the error/rate-limit empty state whenever the active error is a
  // backend rejection AND the accumulated row list is empty. Don't blow
  // away a previously-rendered list on a transient 5xx — the user can
  // retry from the toast.
  //   - 429 → `rate-limited` (countdown + disabled Retry, WEB-289)
  //   - other 4xx / 5xx → `request-failed` (immediate Retry available)
  const hasEmptyErrorState = lastError !== null && events.length === 0;
  // Offline takes precedence over the generic failure copy: when a fetch error
  // is present AND the device reports no connectivity, show the calmer offline
  // state. Rate-limited (429) keeps its own countdown treatment regardless —
  // a 429 is a server signal, not a connectivity one. See the helper for the
  // full precedence table.
  const emptyStateKind = resolveEmptyStateKind(hasEmptyErrorState, lastError, isOnline);
  // Seed the countdown from the parsed Retry-After. `?? undefined` so the
  // empty state's own default (0) applies for non-rate-limited kinds.
  const retryAfterSeconds =
    lastError?.kind === 'rate-limited' ? (lastError.retryAfterSeconds ?? undefined) : undefined;

  // Same map DiscoverActiveFilters reads off this envelope — so empty-state
  // names match the chip row when the payload still carries the id.
  const venueNames = React.useMemo(() => {
    const followedVenues = data?.authenticatedEnvelope?.followedVenues;
    if (!Array.isArray(followedVenues)) return undefined;
    return new Map(followedVenues.map((venue) => [venue.id, venue.name]));
  }, [data?.authenticatedEnvelope?.followedVenues]);

  return (
    <DiscoverListViewView
      events={events}
      total={total}
      cityCode={cityCode}
      cityTimezone={resolvedTimezone}
      now={clock.now}
      isLoading={firstPageLoading}
      isFetchingMore={loadingMore}
      hasMore={hasMore}
      nextPageSize={Math.min(PAGE_SIZE, Math.max(total - events.length, 0))}
      onLoadMore={handleLoadMore}
      onReset={handleReset}
      emptyStateKind={emptyStateKind}
      onRetry={handleRetry}
      retryAfterSeconds={retryAfterSeconds}
      className={className}
      date={params.date}
      params={params}
      venueNames={venueNames}
      tasteMatchedTotal={tasteMatchedTotal}
      matchedTotalUnmeasurable={matchedTotalUnmeasurable}
      showAllExpanded={showAllExpanded}
      showAllDrainFailed={showAllDrainFailed}
      onShowAll={handleShowAll}
      onWidenSearch={handleWidenSearch}
    />
  );
}

/**
 * Fold a fresh `DiscoverEventsResponse` into the existing accumulator. Pure
 * helper exposed for tests so we can verify append-vs-replace semantics
 * without driving RTK Query through `act()`.
 */
export function mergeQueryIntoState(
  prev: PaginationState,
  data: DiscoverEventsResponse,
  currentParamsKey: string,
): PaginationState {
  // Stale paramsKey shouldn't happen (effect resets first) but guard anyway.
  if (prev.paramsKey !== currentParamsKey) {
    return {
      cursor: null,
      events: [...data.events],
      nextCursor: data.meta.cursor,
      total: data.meta.total,
      paramsKey: currentParamsKey,
    };
  }

  // Cursor === null means the request was for the *first* page of this
  // params set — replace any locally accumulated rows so we don't double up.
  if (prev.cursor === null) {
    return {
      ...prev,
      events: dedupeById([...data.events]),
      nextCursor: data.meta.cursor,
      total: data.meta.total,
    };
  }

  // Cursor !== null means we asked for a follow-up page — append.
  return {
    ...prev,
    events: dedupeById([...prev.events, ...data.events]),
    nextCursor: data.meta.cursor,
    total: data.meta.total,
  };
}

function dedupeById(events: DiscoverEvent[]): DiscoverEvent[] {
  const seen = new Set<string>();
  const out: DiscoverEvent[] = [];
  for (const event of events) {
    if (seen.has(event.id)) continue;
    seen.add(event.id);
    out.push(event);
  }
  return out;
}
